import { toast } from 'sonner'

import { useQuery } from '@tanstack/react-query'

import { useState } from 'react'

import { Card, CardContent, CardHeader, CardTitle, PosButton, Spinner } from '@/components/ui'
import { useSession, useSyncState } from '@/contexts/SessionContext'
import { BranchModel } from '@/http/models'
import { cn } from '@/lib/utils'
import { PosModel } from '@/types'

/**
 * First setup of this device: one of its branch's terminals. The branch comes from the Connect code (POS.md §18.2);
 * devices linked before that choose a branch they work in (`Branch.staff_ids`; admins see every branch). The device
 * becomes that terminal and loads the branch's data. The only place a full-screen progress is shown (§15.8 U3);
 * afterwards sync never blocks the till.
 */
export const DeviceSetupPage = () => {
  const { profile, setUpDevice, signOut, linkedBranchId } = useSession()
  const { initialising, progress } = useSyncState()
  const [chosenBranchId, setBranchId] = useState<string | null>(null)
  const branchId = linkedBranchId ?? chosenBranchId
  const [terminalId, setTerminalId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const isAdmin = profile?.roles?.some((role) => role.key === 'admin') ?? false

  const {
    data: allowed = [],
    isLoading,
    error: loadError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['setup-branches', profile?._id, isAdmin],
    retry: false,
    queryFn: async (): Promise<PosModel.Branch[]> => {
      // No `branches` at all means the profile came back without relations, not that the staff member has none.
      if (!isAdmin) {
        if (!profile?.branches) throw new Error('Your branches did not load. Sign out and sign in again.')
        return profile.branches
      }
      const res = await BranchModel.getAllWithTerminals()
      if (!res.success || !res.data) throw new Error(`Could not load the branches: ${res.error?.message ?? 'unknown error'}`)
      return res.data.result
    },
  })
  // A linked branch leaves nothing to choose; staff outside it can't set the device up.
  const branches = linkedBranchId ? allowed.filter((b) => b._id === linkedBranchId) : allowed
  const branch = branches.find((b) => b._id === branchId)
  const terminals = (branch?.terminals ?? []).filter((t) => !t.deleted_at)
  const terminal = terminals.find((t) => t._id === terminalId)

  const onStart = async () => {
    if (!branch || !terminal) return
    setError(null)
    try {
      await setUpDevice(branch._id, terminal)
    } catch (err) {
      setError((err as Error).message)
    }
  }

  const choice = (active: boolean) =>
    cn(
      'rounded-md border p-3 text-left text-sm transition-colors',
      active ? 'border-primary bg-primary text-white' : 'border-input bg-card text-ink hover:bg-subdued'
    )

  return (
    <div className="flex h-dvh items-center justify-center bg-frame p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>Set up this device</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {isLoading ? (
            <Spinner />
          ) : loadError ? null : linkedBranchId && branches.length === 0 ? (
            <p>
              This device was connected for a branch {profile?.name ?? 'you'} {profile?.name ? 'does' : 'do'} not work in. Ask a
              manager to add {profile?.name ? 'them' : 'you'} to that branch, or sign in as someone who works there.
            </p>
          ) : branches.length === 0 ? (
            <p>
              {profile?.name ?? 'You'} {profile?.name ? 'is' : 'are'} not in any branch yet. Ask a manager to add you to a
              branch in the backoffice, then sign in again.
            </p>
          ) : (
            <>
              {linkedBranchId ? (
                <p className="text-muted-foreground">
                  Branch: <span className="font-medium text-ink">{branch?.name}</span>
                </p>
              ) : (
              <div className="space-y-1.5">
                <p className="font-medium text-ink">Branch</p>
                <div className="grid grid-cols-2 gap-2">
                  {branches.map((b) => (
                    <button
                      key={b._id}
                      type="button"
                      className={choice(b._id === branchId)}
                      onClick={() => {
                        setBranchId(b._id)
                        setTerminalId(null)
                      }}
                    >
                      {b.name}
                    </button>
                  ))}
                </div>
              </div>
              )}
              {branch && (
                <div className="space-y-1.5">
                  <p className="font-medium text-ink">Terminal</p>
                  {terminals.length === 0 ? (
                    <p className="text-muted-foreground">This branch has no terminal yet. A manager adds one on the Terminals screen.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {terminals.map((t) => (
                        <button key={t._id} type="button" className={choice(t._id === terminalId)} onClick={() => setTerminalId(t._id)}>
                          T{t.unique_no} · {t.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {initialising && (
            <div className="space-y-1.5">
              <div className="h-2 overflow-hidden rounded-full bg-fill">
                <div className="h-full bg-primary transition-[width]" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-muted-foreground">Loading branch data… {progress}%</p>
            </div>
          )}
          {(loadError?.message ?? error) && <p className="text-destructive">{loadError?.message ?? error}</p>}
          <div className="flex gap-2">
            {loadError ? (
              <PosButton size="touch" className="flex-1" onClick={() => refetch()} disabled={isFetching}>
                Try again
              </PosButton>
            ) : (
              <PosButton size="touch" className="flex-1" onClick={onStart} disabled={!terminal || initialising}>
                {error ? 'Try again' : 'Set up'}
              </PosButton>
            )}
            <PosButton size="touch" variant="secondary" onClick={() => signOut().catch((e: Error) => toast.error(e.message))} disabled={initialising}>
              Sign out
            </PosButton>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
